import { useEffect, type ReactNode } from 'react';
import { getBoolean, fetchAndActivate, ensureInitialized } from 'firebase/remote-config';

import { useApplicationFlags } from 'src/store/AppFlags';
import { firebaseRemoteConfig } from 'src/auth/guard/minimum-version-guard';

export const NOTIFICATIONS_REMOTE_CONFIG_KEY = 'NOTIFICATIONS_FEAT_ENABLED';

type Props = {
  children: ReactNode;
};

/**
 * Keeps the notification kill switch available to every application bundle.
 *
 * The store defaults to false. A previously activated value is read first so
 * offline launches can use Firebase's cache, then an online refresh is
 * attempted. Any initialization/fetch failure therefore remains fail-closed.
 */
export default function NotificationFeatureProvider({ children }: Props) {
  const setEnabled = useApplicationFlags((state) => state.setNotificationsFeatureEnabled);

  useEffect(() => {
    let active = true;

    const applyActivatedValue = () => {
      if (!active) return;
      try {
        setEnabled(getBoolean(firebaseRemoteConfig, NOTIFICATIONS_REMOTE_CONFIG_KEY));
      } catch {
        setEnabled(false);
      }
    };

    const load = async () => {
      try {
        await ensureInitialized(firebaseRemoteConfig);
        applyActivatedValue();
        await fetchAndActivate(firebaseRemoteConfig);
        applyActivatedValue();
      } catch (error) {
        console.warn('[NotificationFeatureProvider] Remote Config unavailable', error);
        applyActivatedValue();
      }
    };

    load();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') load();
    };
    window.addEventListener('online', load);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      active = false;
      window.removeEventListener('online', load);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setEnabled]);

  return <>{children}</>;
}
